import { Form } from "antd"
import clsx from "clsx"
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import Icon from "../../Icon"
import { TextInput } from "../Input/TextInput.tsx"
import { normalizeEmail } from "../../utils/form/normalizeEmail"
import utils from "../../utils/utils"
import ButtonComponent from "../Button"

type FormErrorText = string | string[]

interface RestorePasswordFormValues {
  email: string
}

interface RestorePasswordFormProps {
  error?: string | Record<string, FormErrorText> | null
  onSubmit: (values: RestorePasswordFormValues) => void
  children?: React.ReactNode
}

const alertIcon = "alert_triangle_error" as React.ComponentProps<
  typeof Icon
>["type"]

function RestorePasswordForm({ error, ...props }: RestorePasswordFormProps) {
  const [errorText, setErrorText] = useState<FormErrorText | null>(null)
  const [, forceUpdate] = useState({}) // To disable submit button at the beginning.
  const [isDisabled, setIsDisabled] = useState(true)

  const [form] = Form.useForm()
  const { t } = useTranslation()

  useEffect(() => {
    forceUpdate({})
  }, [])

  useEffect(() => {
    if (error) {
      if (typeof error === "string") {
        setErrorText(error)
      } else {
        setErrorText(Object.values(error)[0])
      }
    }
  }, [error])

  const onFinish = (values: RestorePasswordFormValues) => {
    props.onSubmit(values)
  }

  const onFinishFailed = ({
    errorFields
  }: {
    errorFields?: Array<{ errors: string[] }>
  }) => {
    if (errorFields) setErrorText(errorFields[0]["errors"])
  }

  function handleClearError(
    e: React.ChangeEvent<HTMLInputElement>,
    field: string
  ) {
    form.setFields([
      {
        name: field,
        errors: []
      }
    ])
    setErrorText(null)
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const { name, value } = e.target

    handleClearError(e, name)

    if (utils.validateEmail(value)) {
      if (isDisabled) setIsDisabled(false)
    } else {
      setIsDisabled(true)
    }
  }

  return (
    <Form
      form={form}
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      className="login-form"
      layout="vertical"
    >
      <Form.Item noStyle shouldUpdate={true}>
        {() => (
          <div
            className={clsx("error-text", { "error-text_empty": !errorText })}
          >
            {errorText && <Icon type={alertIcon} role={"icon"} />}
            {errorText}
          </div>
        )}
      </Form.Item>

      <Form.Item
        label={t("Email")}
        name="email"
        validateStatus={errorText ? "error" : undefined}
        rules={[
          { type: "email", message: t("The email you entered is incorrect") }
        ]}
        style={{ marginBottom: 0 }}
        normalize={normalizeEmail}
      >
        <TextInput
          disableAutoCapitalize
          size={"large"}
          placeholder={t("Email address")}
          name="email"
          type="email"
          onChange={(e) => handleChange(e)}
        />
      </Form.Item>

      <ButtonComponent
        size={"large"}
        type="primary"
        htmlType="submit"
        block
        disabled={isDisabled || !form.getFieldValue("email")}
      >
        {t("Send recovery code")}
      </ButtonComponent>

      {props.children}
    </Form>
  )
}

export default RestorePasswordForm
